import { EnvelopeIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { Helmet } from "react-helmet";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export default function Contact() {
  // Add script to set g-recaptcha-response as required
  useEffect(() => {
    // Load reCAPTCHA script
    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    // Add required attribute to g-recaptcha-response
    const setRequired = () => {
      const el = document.getElementById("g-recaptcha-response");
      if (el) {
        el.setAttribute("required", "required");
      }
    };
    window.addEventListener("load", setRequired);
    setTimeout(setRequired, 1000); // fallback in case recaptcha loads late
    return () => {
      window.removeEventListener("load", setRequired);
      document.body.removeChild(script);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Contact DojoTrack | AI Powered Martial Arts CRM</title>
        <meta
          name="description"
          content="Get in touch with DojoTrack. Have questions about our martial arts school management software? Contact our support team for assistance."
        />
        <meta
          name="keywords"
          content="contact dojo track, dojo software support, martial arts management contact, dojo track help, martial arts software support"
        />
        <meta name="author" content="DojoTrack LLC" />
        <meta
          property="og:title"
          content="Contact Us - DojoTrack Martial Arts School Management"
        />
        <meta
          property="og:description"
          content="Get in touch with DojoTrack. Have questions about our martial arts school management software? Contact our support team for assistance."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dojotrack.com/contact" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Contact Us - DojoTrack Martial Arts School Management"
        />
        <meta
          name="twitter:description"
          content="Get in touch with DojoTrack. Have questions about our martial arts school management software? Contact our support team for assistance."
        />
        <link rel="canonical" href="https://dojotrack.com/contact" />
      </Helmet>
      {/* Inline CSS for g-recaptcha-response field */}
      <style>{`
        #g-recaptcha-response {
          display: block !important;
          position: absolute;
          margin: -50px 0 0 0 !important;
          z-index: -999999;
          opacity: 0;
        }
      `}</style>
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:py-20 lg:px-8">
          <div className="mx-auto max-w-2xl space-y-16 divide-y divide-gray-100 dark:divide-gray-700 lg:mx-0 lg:max-w-none">
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-3">
              <div>
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                  <span className="text-gray-900 dark:text-white">Get in </span>
                  <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">touch</span>
                </h2>
                <p className="mt-6 leading-7 text-gray-600 dark:text-gray-400">
                  Have questions about DojoTrack? We're here to help. Send us a
                  message and we'll respond within 24 to 48 hours.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-1 lg:col-span-2 lg:gap-8">
                <div className="rounded-2xl bg-gradient-to-br from-indigo-50/80 to-purple-50/80 dark:from-indigo-900/30 dark:to-purple-900/30 p-10 border border-indigo-200 dark:border-indigo-700 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h3 className="text-base font-semibold leading-7 text-gray-900 dark:text-white">
                    Response Time
                  </h3>
                  <dl className="mt-3 space-y-1 text-sm leading-6 text-gray-600 dark:text-gray-400">
                    <div>
                      <dt className="sr-only">Response Time</dt>
                      <dd>
                        We typically respond within 24 to 48 hours during business
                        days.
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 pt-16 lg:grid-cols-3">
              <div>
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                  <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Contact Form</span>
                </h2>
                <p className="mt-6 leading-7 text-gray-600 dark:text-gray-400">
                  Fill out the form below and we'll get back to you as soon as
                  possible. All fields are required.
                </p>
              </div>
              <form
                action="https://formspree.io/f/xblyewyq"
                method="POST"
                className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 lg:col-span-2"
              >
                <div className="sm:col-span-2">
                  <label
                    htmlFor="name"
                    className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white"
                  >
                    Name *
                  </label>
                  <div className="mt-2.5">
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      minLength={2}
                      maxLength={100}
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 transition-all duration-300 hover:ring-indigo-300 dark:hover:ring-indigo-500"
                      placeholder="Your full name"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white"
                  >
                    Email *
                  </label>
                  <div className="mt-2.5">
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      maxLength={254}
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 transition-all duration-300 hover:ring-indigo-300 dark:hover:ring-indigo-500"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="subject"
                    className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white"
                  >
                    Subject *
                  </label>
                  <div className="mt-2.5">
                    <input
                      type="text"
                      name="subject"
                      id="subject"
                      required
                      minLength={5}
                      maxLength={200}
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 transition-all duration-300 hover:ring-indigo-300 dark:hover:ring-indigo-500"
                      placeholder="What is this regarding?"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="message"
                    className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white"
                  >
                    Message *
                  </label>
                  <div className="mt-2.5">
                    <textarea
                      name="message"
                      id="message"
                      rows={6}
                      required
                      minLength={10}
                      maxLength={2000}
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 transition-all duration-300 hover:ring-indigo-300 dark:hover:ring-indigo-500"
                      placeholder="Please provide details about your inquiry..."
                    />
                  </div>
                </div>
                {/* reCAPTCHA widget */}
                <div className="sm:col-span-2">
                  <div
                    className="g-recaptcha"
                    data-sitekey="6LcsRnorAAAAAMTlkXSo008VZuyDPTD1rnbD-zYO"
                  ></div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    This site is protected by reCAPTCHA and the Google{" "}
                    <a
                      href="https://policies.google.com/privacy"
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://policies.google.com/terms"
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
                    >
                      Terms of Service
                    </a>{" "}
                    apply.
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    className="block w-full rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-center text-sm font-semibold text-white shadow-lg hover:from-indigo-500 hover:to-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                  >
                    Send message
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
